import { signal } from '@angular/core';

import { addDays, addMonths, addWeeks, addYears, startOfDay } from 'date-fns';

import { CalendarModeType } from '../types/calendar';
import { dayRange, monthRange, weekRange, yearRange } from './helpers/date';
import { CamEventRecord } from './models/event-record';
import { CamRecord } from './models/record';
import { CamStore } from './models/store';
import { CamDateRange } from './types';

export type CamCalendarEventName = 'dateRangeChange' | 'modeChange' | 'refresh';

export class CamCalendarEngine {
  public readonly eventStore = new CamStore<CamEventRecord>();
  public readonly resourceStore = new CamStore<CamRecord>();
  public readonly timeRangeStore = new CamStore<CamRecord>();

  public readonly currentDate = signal(new Date());
  public readonly currentMode = signal<CalendarModeType>('week');
  public readonly revision = signal(0);

  public mode: CalendarModeType = 'week';
  public date: Date = new Date();
  public activeView: CamDateRange;
  public startDate: Date;
  public endDate: Date;

  private _listeners = new Map<CamCalendarEventName, Set<(...args: any[]) => void>>();
  private _destroyed = false;

  constructor() {
    this.activeView = this.getRange();
    this.startDate = this.activeView.startDate;
    this.endDate = this.activeView.endDate;

    this.eventStore.changes$.subscribe({ next: () => this.revision.update(value => value + 1) });
    this.timeRangeStore.changes$.subscribe({ next: () => this.revision.update(value => value + 1) });
    this.resourceStore.changes$.subscribe({ next: () => this.revision.update(value => value + 1) });
  }

  public isDestroyed(): boolean {
    return this._destroyed;
  }

  public setMode(mode: CalendarModeType): void {
    if (this.currentMode() === mode) {
      return;
    }
    this.currentMode.set(mode);
    this._syncSnapshot();
    this.trigger('modeChange');
    this.trigger('dateRangeChange');
  }

  public setDate(date: Date): void {
    this.currentDate.set(date);
    this._syncSnapshot();
    this.trigger('dateRangeChange');
  }

  public getActiveView(): CamDateRange {
    return this.getRange();
  }

  public getRange(mode: CalendarModeType = this.currentMode(), date: Date = this.currentDate()): CamDateRange {
    switch (mode) {
      case 'day': {
        const range = dayRange(date);
        return { endDate: range.end, startDate: range.start };
      }
      case 'month': {
        const range = monthRange(date);
        return { endDate: range.end, startDate: range.start };
      }
      case 'year': {
        const range = yearRange(date);
        return { endDate: range.end, startDate: range.start };
      }
      default: {
        const range = weekRange(date);
        return { endDate: range.end, startDate: range.start };
      }
    }
  }

  public shiftNext(amount: number = 1): void {
    this.setDate(this._shift(amount));
  }

  public shiftPrevious(amount: number = 1): void {
    this.setDate(this._shift(-amount));
  }

  public shiftToNow(): void {
    this.setDate(startOfDay(new Date()));
  }

  public setTimeSpan(startDate: Date): void {
    this.setDate(startDate);
  }

  public refresh(): void {
    this.revision.update(value => value + 1);
    this.trigger('refresh');
  }

  public on(eventName: CamCalendarEventName, handler: (...args: any[]) => void): () => void {
    const handlers = this._listeners.get(eventName) ?? new Set();
    handlers.add(handler);
    this._listeners.set(eventName, handlers);
    return () => handlers.delete(handler);
  }

  public trigger(eventName: CamCalendarEventName, payload?: any): void {
    this._listeners.get(eventName)?.forEach(handler => handler(payload));
  }

  public destroy(): void {
    this._destroyed = true;
    this._listeners.clear();
    this.eventStore.destroy();
    this.resourceStore.destroy();
    this.timeRangeStore.destroy();
  }

  private _syncSnapshot(): void {
    this.mode = this.currentMode();
    this.date = this.currentDate();
    this.activeView = this.getRange();
    this.startDate = this.activeView.startDate;
    this.endDate = this.activeView.endDate;
  }

  private _shift(amount: number): Date {
    const date = this.currentDate();
    switch (this.currentMode()) {
      case 'day':
        return addDays(date, amount);
      case 'month':
        return addMonths(date, amount);
      case 'year':
        return addYears(date, amount);
      default:
        return addWeeks(date, amount);
    }
  }
}
